// WaitlistForm.js
import React, { useState } from 'react';
import { ref, push, serverTimestamp } from 'firebase/database';
import { database } from '../firebase';

const WaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset states
    setError('');
    
    // Validate email
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Add email to waitlist in Firebase
      const waitlistRef = ref(database, 'waitlist');
      await push(waitlistRef, {
        email: email.toLowerCase().trim(),
        createdAt: serverTimestamp(),
        source: window.location.pathname
      });
      
      // Show success message
      setSuccess(true);
      setEmail('');
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      setError('Sorry, something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Join our Waitlist</h2>
      
      <p style={styles.description}>
        We've reached our user capacity for now. Join our waitlist to be notified when 
        we open registration again.
      </p>
      
      {success ? (
        <div style={styles.successContainer}>
          <div style={styles.successIcon}>✓</div>
          <h3 style={styles.successHeading}>You're on the list!</h3>
          <p style={styles.successText}>
            Thanks for joining our waitlist. We'll notify you as soon as registration opens again.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.errorMessage}>{error}</div>}
          
          <div style={styles.inputGroup}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              style={styles.input}
              disabled={isSubmitting}
            />
            
            <button 
              type="submit" 
              style={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
            </button>
          </div>
          
          <p style={styles.privacyText}>
            We'll only use your email to notify you when registration opens.
          </p>
        </form>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '500px',
    margin: '0 auto',
    padding: '24px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  heading: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '16px',
    color: '#0F172A',
    textAlign: 'center',
  },
  description: {
    fontSize: '16px',
    color: '#64748B',
    marginBottom: '24px',
    lineHeight: '1.5',
    textAlign: 'center',
  },
  form: {
    width: '100%',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '16px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #CBD5E1',
    boxSizing: 'border-box',
  },
  submitButton: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  errorMessage: {
    padding: '12px',
    backgroundColor: '#FFEBEE',
    color: '#D32F2F',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  privacyText: {
    fontSize: '14px',
    color: '#94A3B8',
    textAlign: 'center',
    marginTop: '8px',
  },
  successContainer: {
    textAlign: 'center',
    padding: '20px 0',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    backgroundColor: '#10B981',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 20px',
    fontSize: '32px',
  },
  successHeading: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: '12px',
  },
  successText: {
    fontSize: '16px',
    color: '#64748B',
    lineHeight: '1.5',
  },
};

export default WaitlistForm;